import { useEffect, useState } from 'react';
import { useCalendarStore } from '@/store/calendarStore';
import { useAuth } from '../contexts/AuthContext';
import CalendarHeader from '@/components/CalendarHeader';
import DayView from '@/components/DayView';
import WeekView from '@/components/WeekView';
import MonthView from '@/components/MonthView';
import YearView from '@/components/YearView';
import EventForm from '@/components/EventForm';
import { FiPlus, FiDownload, FiUpload } from 'react-icons/fi';
import { exportAllData, importData } from '@/utils/storage';
import { loadExamplesIfEmpty } from '@/utils/loadExamples';

/**
 * Página principal del Calendario
 * Contiene toda la funcionalidad del calendario Gregoriano/Hebreo
 */
export default function Calendar() {
  const { view, initialize } = useCalendarStore();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('evento');
  const [showMenu, setShowMenu] = useState(false);

  // Verificar permisos del usuario
  const canCreateCitas = user?.rol === 'administrador' || user?.modulePermissions?.some(
    p => p.moduleName === 'calendario' && p.roleName === 'calendarizador'
  );
  const canCreateLecturas = canCreateCitas; // Mismos permisos

  useEffect(() => {
    console.log('🎯 [CALENDAR] ========== INICIANDO CALENDARIO ==========');
    console.log('🎯 [CALENDAR] Cargando datos de ejemplo si es necesario...');
    
    // Cargar datos de ejemplo si no hay datos
    const loaded = loadExamplesIfEmpty();
    console.log('🎯 [CALENDAR] Datos de ejemplo cargados:', loaded);
    
    // Inicializar el store al cargar la aplicación
    console.log('🎯 [CALENDAR] Llamando a initialize()...');
    initialize();
    
    if (loaded) {
      console.log('🎉 [CALENDAR] Datos de ejemplo cargados! Navega a Nov 7-8, 2025 para verlos');
    }
  }, [initialize]);

  const handleExport = () => {
    const data = exportAllData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendario-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result;
          if (typeof result === 'string') {
            if (importData(result)) {
              alert('Datos importados exitosamente');
              initialize();
            } else {
              alert('Error al importar datos');
            }
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleAddClick = (type) => {
    setFormType(type);
    setShowForm(true);
    setShowMenu(false);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header de la página */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              📅 Calendario
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Sistema dual Gregoriano / Hebreo Caraíta
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="p-2 sm:p-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
              title="Exportar datos"
            >
              <FiDownload className="w-5 h-5" />
              <span className="hidden sm:inline">Exportar</span>
            </button>
            <button
              onClick={handleImport}
              className="p-2 sm:p-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
              title="Importar datos"
            >
              <FiUpload className="w-5 h-5" />
              <span className="hidden sm:inline">Importar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Controles del Calendario */}
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <CalendarHeader />
      </div>
      
      {/* Vista del Calendario */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 pb-4">
        {view === 'day' && <DayView />}
        {view === 'week' && <WeekView />}
        {view === 'month' && <MonthView />}
        {view === 'year' && <YearView />}
      </div>

      {/* Botón flotante para agregar */}
      <div className="fixed bottom-8 right-8 z-30">
        <div className="relative">
          {showMenu && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl p-2 space-y-2 min-w-[200px]">
              {/* Solo mostrar Nueva Cita si tiene permisos */}
              {canCreateCitas && (
                <button
                  onClick={() => handleAddClick('cita')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  📅 Nueva Cita
                </button>
              )}
              
              {/* Solo mostrar Nueva Lectura si tiene permisos */}
              {canCreateLecturas && (
                <button
                  onClick={() => handleAddClick('lectura')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  📖 Nueva Lectura
                </button>
              )}
              
              {/* Nuevo Evento siempre visible para usuarios autenticados */}
              <button
                onClick={() => handleAddClick('evento')}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
              >
                ⭐ Nuevo Evento
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
          >
            <FiPlus className={`w-6 h-6 sm:w-8 sm:h-8 transition-transform ${showMenu ? 'rotate-45' : ''}`} />
          </button>
        </div>
      </div>

      {/* Modal de formulario */}
      {showForm && (
        <EventForm
          type={formType}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}











